import { useCallback } from 'react';
import useShortcut from 'Hooks/useShortcut';

// Drop this into any modal/form that already has its own save/cancel
// handlers to get the standard shortcuts for free: mod+S and mod+Enter
// trigger onSave (unless canSave is false), Escape triggers onCancel.
// Wrap the modal with useShortcutScope first and pass its id as `scope`
// so these only fire while that modal is the top-most one on screen.
// saveLabel/cancelLabel only affect how these show up in the shortcuts
// help panel (mod+/) - override them for modals that say "Create" instead
// of "Save", etc.
//
// enterToSave opts into a plain, unmodified Enter also triggering onSave -
// the convention several single-field modals (rename, assign, create role/
// credential) relied on before this system existed. It's opt-in rather than
// default because most create modals have multiple fields and Enter should
// move focus, not submit. Enter is still suppressed inside a multiline
// editor (textarea/contentEditable) regardless - see
// Context/Shortcut's shouldSkipInEditableTarget.
const useFormShortcuts = ({
  onSave, onCancel, canSave = true, scope = 'global', saveLabel = 'Save', cancelLabel = 'Cancel', enterToSave = false
} = {}) => {
  const handleSave = useCallback((event) => {
    if (!canSave) return;
    onSave(event);
  }, [onSave, canSave]);

  useShortcut('mod+s', handleSave, { scope, enabled: Boolean(onSave), description: saveLabel });
  useShortcut('mod+Enter', handleSave, { scope, enabled: Boolean(onSave), description: saveLabel });
  useShortcut('Enter', handleSave, { scope, enabled: enterToSave && Boolean(onSave), description: saveLabel });
  useShortcut('Escape', onCancel, { scope, enabled: Boolean(onCancel), description: cancelLabel });
};

export default useFormShortcuts;
